import { EventEmitter } from 'events'
import { DAVClient, DAVAddressBook } from 'libs/tsdav'
import ical from 'ical.js'
import { convertVCardToContact } from './vCardConverter'
// import { updateToken } from 'services/jwt'

import conf from 'conf'
import { v4 as uuidv4 } from 'uuid'

import PersonalContactDB from './PersonalContactDB'

const contactDB = new PersonalContactDB()

// const localForage = require('localforage')

const cardDavServerUrl = (conf.SERVER.davURL ?? conf.SERVER.BASE_URL) + '/dav/addressbooks'

const contactsUpdatedEvent = new EventEmitter()
const syncEvent = new EventEmitter()

const syncFlag = { synchronizing: false }

interface UserInfo {
  email?: string
  name?: string
}

interface MyInfo {
  email: string
  display_name?: string
  name?: string
}

interface AddressBook {
  url: string
  syncToken?: string
  displayName?: string
  ctag?: string
  isSync: boolean
  isDefault: boolean
  isShared: boolean
  folderType: string
}

interface ContactData {
  serverId: string
  etag: string
  contact: any
}

interface PhotoData {
  serverId: string
  photo: string
}

interface DAVResponse {
  href?: string
  props?: {
    getetag?: string
    getctag?: string
    syncToken?: string
    displayname?: {
      _cdata?: string
    }
    addressData?: {
      _cdata?: string
    }
  }
}

const userInfo: UserInfo = {}
let client: DAVClient | null = null

let waitingSyncAddressBooks: AddressBook[] = []
let addressBooks: AddressBook[] = []
const contacts: Record<string, ContactData> = {} // { serverId: { serverId, etag, data } }

export const logout = async (): Promise<void> => {
  client = null
  addressBooks = []
  Object.keys(contacts).forEach((key) => {
    delete contacts[key]
  })
  Object.keys(userInfo).forEach((key) => {
    delete (userInfo as any)[key]
  })
  waitingSyncAddressBooks = []

  contactDB.clear('addressBook')
  contactDB.clear('contact')
  contactDB.clear('photo')
}

export const initClient = async (myInfo: MyInfo | null): Promise<void> => {
  if (myInfo != null) {
    userInfo.email = myInfo.email
    userInfo.name = myInfo.display_name || myInfo.name
    if (!userInfo.name || userInfo.name.length === 0) {
      userInfo.name = userInfo.email.split('@')[0]
    }
  }

  const folderList = await contactDB.getAllData('addressBook')
  folderList.forEach((folder: AddressBook) => {
    addressBooks.push(folder)
  })

  const contactList = await contactDB.getAllData('contact')
  contactList.forEach((contact: ContactData) => {
    contacts[contact.serverId] = {
      serverId: contact.serverId,
      etag: contact.etag,
      contact: contact.contact,
    }
  })

  client = new DAVClient({
    serverUrl: cardDavServerUrl,
    credentials: { username: '', password: '' },
    authMethod: 'Custom',
    authFunction: async (_credentials: any) => {
      // const expiresIn = await localForage.getItem('app.user.refreshExpiresIn')
      // if (expiresIn != null && expiresIn * 1000 < Date.now()) {
      //   await updateToken()
      // }

      // const accessToken = await localForage.getItem('app.user.token')
      // // tokenType은 Bearer로 고정.
      // const tokenType = 'Bearer'
      // // const tokenType = await localForage.getItem('app.user.tokenType')
      // return {
      //   Authorization: `${tokenType} ${accessToken}`,
      // }
      return {}
    },
    defaultAccountType: 'carddav',
  })

  try {
    await client.login()

    await fetchAddressBooks()
  } catch (error) {
    console.error('Failed to login to CardDAV server:', error)
  }
}

const fetchAddressBooks = async (): Promise<void> => {
  if (!client) return

  const inAddressBooks = await client.fetchAddressBooks()

  let oldAddressBooks = [...addressBooks]

  inAddressBooks.forEach((addressBook: DAVAddressBook) => {
    const folder = addressBooks.find((folder) => folder.url === addressBook.url)
    if (folder) {
      folder.folderType = 'contact' // Migration 처리. 추후 삭제.
      const ctag = typeof addressBook.ctag === 'string' ? addressBook.ctag : ''
      const syncToken = typeof addressBook.syncToken === 'string' ? addressBook.syncToken : ''
      const displayName = typeof addressBook.displayName === 'string' ? addressBook.displayName : ''
      if (folder.ctag !== ctag) {
        contactDB.putData('addressBook', {
          ...folder,
          syncToken,
          ctag,
          displayName,
        })
      }
      oldAddressBooks = oldAddressBooks.filter((folder) => folder.url !== addressBook.url)
    } else {
      const isDefault = addressBook.url.endsWith('/Default/')
      const newFolder: AddressBook = {
        url: addressBook.url,
        syncToken: typeof addressBook.syncToken === 'string' ? addressBook.syncToken : '',
        displayName: typeof addressBook.displayName === 'string' ? addressBook.displayName : '',
        ctag: typeof addressBook.ctag === 'string' ? addressBook.ctag : '',
        isSync: isDefault,
        isDefault,
        isShared: false,
        folderType: 'contact',
      }
      addressBooks.push(newFolder)
      contactDB.addData('addressBook', newFolder)
    }
  })

  // Folder 가 지워진 경우 처리내역.
  oldAddressBooks.forEach((folder) => {
    contactDB.deleteData('addressBook', folder.url)
    addressBooks = addressBooks.filter((addressBook) => addressBook.url !== folder.url)
    const url = new URL(folder.url)
    const prefix = url.pathname
    Object.keys(contacts).forEach((key) => {
      if (key.startsWith(prefix)) {
        delete contacts[key]
        contactDB.deleteData('contact', key)
      }
    })
  })

  _fetchContacts()
}

const getContactInfoList = async (): Promise<{
  needFetchContacts: string[]
  hasDeleted: boolean
}> => {
  if (!client) return { needFetchContacts: [], hasDeleted: false }

  const xml = `<?xml version="1.0" encoding="utf-8"?>
<card:addressbook-query xmlns:card="urn:ietf:params:xml:ns:carddav" xmlns:d="DAV:">
  <d:prop>
    <d:getetag/>
  </d:prop>
</card:addressbook-query>`

  /* <card:filter>
<card:prop-filter name="FN"/>
</card:filter> */

  const response: DAVResponse[] = await client.davRequest({
    url: addressBooks[0].url,
    convertIncoming: false,
    init: {
      method: 'REPORT',
      body: xml,
      headers: {
        Depth: '1',
      },
    },
  })
  console.log('getContactInfoList -', response)

  const needFetchContacts: string[] = []
  const deletedContacts = new Set(Object.keys(contacts) || [])

  response.forEach((item) => {
    const serverId = item.href
    if (!serverId) {
      // 어떤 contact도 없는 경우 response에 정보없는 아이템만 있음.
      return
    }
    const etag = item.props?.getetag || ''

    const c = contacts[serverId]
    if (c) {
      if (c.etag !== etag) {
        needFetchContacts.push(serverId) //Update Contact
      }
    } else {
      needFetchContacts.push(serverId) //New Contact
    }
    deletedContacts.delete(serverId)
  })

  deletedContacts.forEach((id) => {
    delete contacts[id]
    contactDB.deleteData('contact', id)
  })

  return { needFetchContacts, hasDeleted: deletedContacts.size > 0 }
}

const fetchContactsData = async (serverIds: string[]): Promise<void> => {
  if (serverIds.length === 0) {
    _completeSync()
    return
  }

  let currentIds: string[] | null = null
  let nextIds: string[] = []
  if (serverIds.length > 9) {
    currentIds = serverIds.slice(0, 10)
    nextIds = serverIds.slice(10)
  } else {
    currentIds = serverIds
  }

  const xmlPrefix = `<?xml version="1.0" encoding="utf-8"?>
<card:addressbook-multiget xmlns:d="DAV:" xmlns:card="urn:ietf:params:xml:ns:carddav">
  <d:prop>
    <d:getetag/>
    <card:address-data/>
  </d:prop>`
  const xmlSuffix = `</card:addressbook-multiget>`

  const xml = xmlPrefix + currentIds.map((id) => `<d:href>${id}</d:href>`).join('') + xmlSuffix

  if (!client) return

  const response: DAVResponse[] = await client.davRequest({
    url: addressBooks[0].url,
    convertIncoming: false,
    init: {
      method: 'REPORT',
      body: xml,
    },
  })

  response.forEach((item) => {
    const serverId = item.href
    const etag = item.props?.getetag
    const data = item.props?.addressData?._cdata
    if (!serverId || !etag || !data) return

    console.log('fetchContactsData - ', data)

    let vCard = null
    try {
      vCard = ical.parse(data)
    } catch (error) {
      console.error('Failed to parse vCard (fetchContactsData):', error, data)
      return
    }

    const { result, allValues, emails, phones } = convertVCardToContact(vCard)

    result.hasPhoto = result.photo !== null
    const photo = result.photo
    delete result.photo

    contacts[serverId] = {
      serverId,
      etag,
      contact: result,
    }
    contactDB.putData('contact', {
      serverId,
      etag,
      emails,
      phones,
      allValues,
      contact: result,
    })
    if (photo) {
      contactDB.putData('photo', {
        serverId,
        photo,
      })
    }
  })

  if (nextIds.length > 0) {
    fetchContactsData(nextIds)
  } else {
    _completeSync()
  }

  _notifyContactsUpdated()
}

const _fetchContacts = async (): Promise<void> => {
  const { needFetchContacts, hasDeleted } = await getContactInfoList()
  console.log('needFetchContacts', needFetchContacts)

  if (needFetchContacts.length > 0) {
    fetchContactsData(needFetchContacts)
  } else if (hasDeleted) {
    _notifyContactsUpdated()
    _completeSync()
  } else {
    _completeSync()
  }
}

const _notifyContactsUpdated = (): void => {
  contactsUpdatedEvent.emit('contactsUpdated')
}

export const addContactsUpdatedListener = (callback: () => void): void => {
  contactsUpdatedEvent.on('contactsUpdated', callback)
}

export const removeContactsUpdatedListener = (callback: () => void): void => {
  contactsUpdatedEvent.off('contactsUpdated', callback)
}

const _completeSync = (): void => {
  _notifySync(false)

  waitingSyncAddressBooks.forEach((addressBook) => {
    contactDB.putData('addressBook', addressBook)
  })
  waitingSyncAddressBooks = []
}

const _notifySync = (synchronizing: boolean = false): void => {
  syncFlag.synchronizing = synchronizing
  syncEvent.emit('sync')
}

export const addSyncListener = (callback: () => void): void => {
  syncEvent.on('sync', callback)
}

export const removeSyncListener = (callback: () => void): void => {
  syncEvent.off('sync', callback)
}

export const isSynchronizing = (): boolean => {
  return syncFlag.synchronizing
}

export const syncChanges = async (): Promise<void> => {
  if (syncFlag.synchronizing) {
    return
  }

  _notifySync(true)

  const xml = `<?xml version='1.0' encoding='UTF-8' ?>
<propfind xmlns="DAV:">
    <prop xmlns="DAV:">
        <CARD:max-resource-size xmlns:CARD="urn:ietf:params:xml:ns:carddav" />
        <CARD:supported-address-data xmlns:CARD="urn:ietf:params:xml:ns:carddav" />
        <supported-report-set xmlns="DAV:" />
        <n1:getctag xmlns:n1="http://calendarserver.org/ns/" />
        <sync-token xmlns="DAV:" />
        <displayname xmlns="DAV:" />
    </prop>
</propfind>`

  if (!client) return

  const response: DAVResponse[] = await client.davRequest({
    url: addressBooks[0].url,
    convertIncoming: false,
    init: {
      method: 'PROPFIND',
      body: xml,
      headers: {
        Depth: '0',
      },
    },
  })
  console.log('syncChanges - ', response)

  if (addressBooks[0].ctag !== response[0].props?.getctag) {
    console.log('ctag is changed')

    addressBooks[0].ctag = response[0].props?.getctag
    addressBooks[0].syncToken = response[0].props?.syncToken

    waitingSyncAddressBooks.push({
      ...addressBooks[0],
      ctag: response[0].props?.getctag || '',
      syncToken: response[0].props?.syncToken || '',
      displayName: response[0].props?.displayname?._cdata || '',
    })

    _fetchContacts()
  } else {
    _completeSync()
  }
}

export const saveContact = async (
  id: string | null,
  etag: string,
  saveData: string,
): Promise<void> => {
  if (!client) return

  let response = null
  if (!id) {
    const filename = uuidv4() + '.vcf'
    response = await client.createVCard({
      addressBook: addressBooks[0],
      filename,
      vCardString: saveData,
    })
  } else {
    response = await client.updateVCard({
      vCard: {
        url: addressBooks[0].url + id.split('/').pop(),
        etag,
        data: saveData,
      },
    })
  }
  syncChanges()
  console.log('saveContact - ', response)
}

export const deleteContact = async (id: string, etag: string): Promise<void> => {
  if (!client) return

  const response = await client.deleteVCard({
    vCard: {
      url: addressBooks[0].url + id.split('/').pop(),
      etag,
    },
  })
  console.log('deleteContact - ', response)
  syncChanges()
}

export const getUserInfo = (): UserInfo => {
  return userInfo
}

export const getAddressBooks = (): AddressBook[] => {
  return addressBooks
}

export const enableSync = (folderId: string, isSync: boolean): void => {
  const addressBook = addressBooks.find((addressBook) => addressBook.url === folderId)
  if (addressBook) {
    addressBook.isSync = isSync
    contactDB.putData('addressBook', addressBook)

    _notifyContactsUpdated()
  }
}

const _sortContacts = (contacts: ContactData[] | null): ContactData[] => {
  if (!contacts) {
    return []
  }
  return contacts.sort((a, b) => {
    const aFullName = a.contact.person.full_name
    const bFullName = b.contact.person.full_name
    if (aFullName && bFullName) {
      return aFullName.localeCompare(bFullName)
    }
    if (aFullName) {
      return -1
    }
    if (bFullName) {
      return 1
    }
    return 0
  })
}

export const getContacts = (): ContactData[] => {
  let contactList = Object.values(contacts)
  addressBooks.forEach((addressBook) => {
    if (!addressBook.isSync) {
      const path = new URL(addressBook.url).pathname
      contactList = contactList.filter((contact) => !contact.serverId.startsWith(path))
    }
  })

  return _sortContacts(contactList)
}

export const getPhoto = async (serverId: string): Promise<PhotoData | null> => {
  try {
    return await contactDB.getData('photo', serverId)
  } catch (err) {
    console.log('getPhoto error - ', err)
    return null
  }
}

export const searchContact = async (searchText: string): Promise<ContactData[]> => {
  const contacts = await contactDB.searchContact(searchText)
  return _sortContacts(contacts)
}

export const searchContactByEmail = async (
  email: string,
  count: number = -1,
): Promise<ContactData[]> => {
  const inContacts = await contactDB.searchContactByEmail(email, count)
  return _sortContacts(inContacts)
}

export const searchContactNameEmail = async (
  text: string,
  count: number = -1,
): Promise<ContactData[]> => {
  const inContacts = await contactDB.searchContactNameEmail(text, count)
  return _sortContacts(inContacts)
}

export const searchContactByPhone = async (
  phone: string,
  count: number = -1,
): Promise<ContactData[]> => {
  const inContacts = await contactDB.searchContactByPhone(phone, count)
  return _sortContacts(inContacts)
}

export const searchContactsEmailsPhones = async (
  emails: string[],
  phones: string[],
  count: number = -1,
): Promise<ContactData[]> => {
  const inContacts: Record<string, ContactData> = {}
  for (const email of emails) {
    const contacts = await searchContactByEmail(email, count)
    for (const c of contacts) {
      inContacts[c.serverId] = c
    }
  }

  for (const phone of phones) {
    const contacts = await searchContactByPhone(phone, count)
    for (const c of contacts) {
      inContacts[c.serverId] = c
    }
  }

  return _sortContacts(Object.values(inContacts))
}

export const getPhotoWithEmail = async (email: string): Promise<string | null> => {
  const inContacts = await searchContactByEmail(email, 1)
  const serverId = inContacts?.[0]?.serverId
  if (serverId) {
    const photo = await getPhoto(serverId)
    if (photo?.photo && !photo.photo.startsWith('data:image/')) {
      return 'data:image/png;base64,' + photo.photo
    }
    return photo?.photo || null
  }
  return null
}

export const searchContactOnline = async (
  text: string /* , limit: number = -1 */,
): Promise<ContactData[]> => {
  /*
   * 온라인 검색 : 일부 항목은 검색 불가.
   * 검색 불가 항목 : NICKNAME, X-ABRELATEDNAMES, X-ABDATE
   * 추후 사용시 반영.
   */
  const xml = `<?xml version="1.0" encoding="utf-8"?>
<card:addressbook-query xmlns:card="urn:ietf:params:xml:ns:carddav" xmlns:d="DAV:">
  <d:prop>
    <d:getetag/>
    <card:address-data/>
  </d:prop>

  <card:filter>
    <card:prop-filter name="N">
      <card:text-match match-type="contains">${text}</card:text-match>
    </card:prop-filter>
    <card:prop-filter name="X-PHONETIC-FIRST-NAME">
      <card:text-match match-type="contains">${text}</card:text-match>
    </card:prop-filter>
    <card:prop-filter name="X-PHONETIC-LAST-NAME">
      <card:text-match match-type="contains">${text}</card:text-match>
    </card:prop-filter>
    <card:prop-filter name="X-PHONETIC-MIDDLE-NAME">
      <card:text-match match-type="contains">${text}</card:text-match>
    </card:prop-filter>
    <card:prop-filter name="FN">
      <card:text-match match-type="contains">${text}</card:text-match>
    </card:prop-filter>
    <card:prop-filter name="EMAIL">
      <card:text-match match-type="contains">${text}</card:text-match>
    </card:prop-filter>
    <card:prop-filter name="TEL">
      <card:text-match match-type="contains">${text}</card:text-match>
    </card:prop-filter>
    <card:prop-filter name="ADR">
      <card:text-match match-type="contains">${text}</card:text-match>
    </card:prop-filter>
    <card:prop-filter name="ORG">
      <card:text-match match-type="contains">${text}</card:text-match>
    </card:prop-filter>
    <card:prop-filter name="TITLE">
      <card:text-match match-type="contains">${text}</card:text-match>
    </card:prop-filter>
    <card:prop-filter name="NOTE">
      <card:text-match match-type="contains">${text}</card:text-match>
    </card:prop-filter>
    <card:prop-filter name="BDAY">
      <card:text-match match-type="contains">${text}</card:text-match>
    </card:prop-filter>
    <card:prop-filter name="ANNIVERSARY">
      <card:text-match match-type="contains">${text}</card:text-match>
    </card:prop-filter>
    <card:prop-filter name="URL">
      <card:text-match match-type="contains">${text}</card:text-match>
    </card:prop-filter>
  </card:filter>
</card:addressbook-query>`

  if (!client) return []

  const response: DAVResponse[] = await client.davRequest({
    url: addressBooks[0].url,
    convertIncoming: false,
    init: {
      method: 'REPORT',
      body: xml,
      headers: {
        Depth: '1',
      },
    },
  })
  console.log('searchContactOnline raw - ', response)

  if (!response || response.length === 0 || (response.length === 1 && !response[0].href)) {
    // 검색결과 없음.
    return []
  }

  const results: ContactData[] = []
  response.forEach((item) => {
    const serverId = item.href
    const etag = item.props?.getetag
    const data = item.props?.addressData?._cdata
    if (!serverId || !etag || !data) return

    let vCard = null
    try {
      vCard = ical.parse(data)
    } catch (error) {
      console.error('Failed to parse vCard (searchContactOnline):', error, data)
      return
    }
    const { result } = convertVCardToContact(vCard)

    result.hasPhoto = result.photo !== null

    results.push({
      serverId,
      etag,
      contact: result,
    })
  })

  return results
}
